import { useEffect, useState } from 'react';
import { Alert, Button, Snackbar, Stack } from '@mui/material';

import { BUILD_ID, fetchDeployedBuildId } from '../appVersion';

// Gentle on purpose: this runs in every tab a user leaves open all day, and
// nothing about it is urgent — a build that went live fifteen minutes ago is
// still news to a tab from last month. The interval ticks only while the tab
// is visible and re-checks immediately on refocus, which is what catches the
// common "came back to yesterday's tab" case. Same visibility-gated shape as
// useLiveEvents, at a far lower frequency.
const POLL_MS = 15 * 60 * 1000;

// Floor on how often a check may actually fire, whatever triggered it. The
// refocus hook is the reason this exists: someone who lives in their tabs
// switches back dozens of times an hour, and without a floor every one of those
// switches is a request — hundreds a day, per open tab, for news that keeps.
const MIN_CHECK_GAP_MS = 60 * 1000;

export interface NewVersionNoticeProps {
  /**
   * Build id compiled into the running bundle. Defaults to the real one; only
   * tests pass it, because `__BUILD_ID__` is a compile-time literal and cannot
   * be restubbed per test.
   */
  buildId?: string;
}

/**
 * Non-blocking notice that a newer build is live, with a button to reload.
 *
 * Never reloads on its own. The user may be mid-form, and silently discarding
 * their work would be a worse bug than the stale copy this exists to fix.
 *
 * Mounted once, app-wide, in main.tsx — including on the public routes, which
 * go just as stale as the dashboard does.
 */
const NewVersionNotice = ({ buildId = BUILD_ID }: NewVersionNoticeProps) => {
  // The deployed id we have something to say about, and the one the user has
  // already waved away. Tracking the id (not just a boolean) means a SECOND
  // deploy later the same day prompts again instead of staying dismissed.
  const [liveBuildId, setLiveBuildId] = useState<string | null>(null);
  const [dismissedBuildId, setDismissedBuildId] = useState<string | null>(null);

  useEffect(() => {
    // Local dev, or a bundle built before version.json existed: there is
    // nothing to compare against, so do not even poll.
    if (buildId === '') return;

    let cancelled = false;
    let lastCheckedAt = 0;
    const controller = new AbortController();

    const check = async (): Promise<void> => {
      if (document.visibilityState !== 'visible') return;
      const startedAt = Date.now();
      // `>= lastCheckedAt` matters as much as the gap itself: an NTP step, a
      // laptop resume or a user changing the clock can move Date.now()
      // backwards, and a bare subtraction would then suppress every check in
      // this tab until real time caught back up.
      if (
        startedAt >= lastCheckedAt
        && startedAt - lastCheckedAt < MIN_CHECK_GAP_MS
      ) return;
      lastCheckedAt = startedAt;
      const deployed = await fetchDeployedBuildId(controller.signal);
      // `null` is every failure path (missing, unreachable, malformed): stay
      // silent and try again on the next tick.
      if (cancelled || deployed === null) return;
      // Clearing on a match matters as much as setting on a difference: a
      // rollback can redeploy the very build this tab is running, and the
      // notice has to withdraw rather than go on claiming a newer one exists.
      setLiveBuildId(deployed === buildId ? null : deployed);
    };

    void check();
    const timer = setInterval(() => {
      void check();
    }, POLL_MS);
    const onVisibilityChange = () => {
      void check();
    };
    document.addEventListener('visibilitychange', onVisibilityChange);

    return () => {
      cancelled = true;
      controller.abort();
      clearInterval(timer);
      document.removeEventListener('visibilitychange', onVisibilityChange);
    };
  }, [buildId]);

  if (liveBuildId === null || liveBuildId === dismissedBuildId) return null;

  return (
    // Anchored at the top, where nothing else in the app lives: every other
    // Snackbar sits on the bottom edge (most pass bottom-center explicitly, the
    // rest take MUI's bottom-left default), and MUI does not stack Snackbars —
    // two at one anchor just overlap. This one stays up until dismissed, so at
    // any bottom anchor it would be the thing every transient toast lands on.
    <Snackbar open anchorOrigin={{ vertical: 'top', horizontal: 'center' }}>
      <Alert
        severity="info"
        onClose={() => setDismissedBuildId(liveBuildId)}
        sx={{ width: '100%' }}
      >
        <Stack direction="row" spacing={2} alignItems="center">
          <span>A newer version of the dashboard is available.</span>
          <Button
            size="small"
            variant="outlined"
            color="inherit"
            onClick={() => window.location.reload()}
          >
            Reload
          </Button>
        </Stack>
      </Alert>
    </Snackbar>
  );
};

export default NewVersionNotice;
